import type { AppStore } from 'app/state/store'
import { setAccessToken } from '../state/commonSlice'
import { ApiError, UploadFileOptions } from '../types'

const contentType = 'application/json'

type GetTokenFn = () => Promise<string>
let getToken: GetTokenFn = async () => ''

const hasAuthToken = async () => {
  return (await getToken()) !== ''
}

const setGetTokenFn = (accessTokenFn: GetTokenFn, store: AppStore): void => {
  getToken = async () => {
    const accessToken = await accessTokenFn()
    if (accessToken !== store.getState().common.accessToken) {
      store.dispatch(setAccessToken(accessToken))
    }
    return accessToken
  }

  // call getToken to possibly update token in redux store
  getToken().catch((error) => {
    console.error('setGetTokenFn: failed to update token', error)
  })
}

type GetAuthHeaderFn = () => Promise<string>
let getAuthHeader: GetAuthHeaderFn = async (): Promise<string> => {
  const token = await getToken()
  return `Bearer ${token}`
}
const setGetAuthHeaderFn = (authHeaderFn: GetAuthHeaderFn) => {
  getAuthHeader = authHeaderFn
}

export function buildCustomOpts({ accessToken }: { accessToken?: string }) {
  if (!accessToken) return undefined

  return {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  }
}

const returnJsonIfOkOrThrow = async (response: Response) => {
  if (response.ok) {
    if (response.status === 204) return
    const contentType = response.headers.get('content-type')
    if (contentType?.startsWith('text/plain')) {
      return response.text()
    }
    return response.json()
  } else {
    const errorResponse: ApiError = await response.json()
    // TODO: This is a hack. We need to standardize error object in different sections
    // Builder expects the API to return a Error object with a message
    // Workspace uses a plain object.
    throw { ...errorResponse, status: errorResponse.status ?? response.status, message: errorResponse.error }
  }
}

export interface RequestOpts extends RequestInit {
  anonymous?: boolean
  signal?: AbortSignal
}

const getRequest = async (url: string, opts?: RequestOpts | RequestInit | null) => {
  const request = new Request(
    url,
    await buildRequestInit(opts, {
      method: 'GET',
      headers: {
        Accept: contentType
      }
    })
  )
  const { signal = undefined } = opts || {}
  return fetch(request, { signal }).then(returnJsonIfOkOrThrow)
}

const getRequestFile = async (url: string, opts?: RequestOpts): Promise<Blob> => {
  const request = new Request(
    url,
    await buildRequestInit(opts, {
      method: 'GET',
      headers: {
        Accept: contentType
      }
    })
  )
  return fetch(request).then((response) => response.blob())
}

const getRequestStatus = async (url: string, opts?: RequestInit) => {
  const request = new Request(
    url,
    await buildRequestInit(opts, {
      method: 'GET',
      headers: {
        Accept: contentType
      }
    })
  )
  return fetch(request).then((response) => response.status)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const postRequest = async (url: string, payload: any, opts?: RequestInit, callback?: (response: Response) => any) => {
  const request = new Request(
    url,
    await buildRequestInit(opts, {
      method: 'POST',
      headers: {
        Accept: contentType,
        'Content-Type': contentType
      },
      body: JSON.stringify(payload)
    })
  )
  return fetch(request).then(callback ? callback : returnJsonIfOkOrThrow)
}

const postRequestFile = async <T>(
  url: string,
  payload: BodyInit,
  opts?: RequestInit,
  callback?: (response: Response) => T
) => {
  const request = new Request(
    url,
    await buildRequestInit(opts, {
      method: 'POST',
      body: payload
    })
  )
  return fetch(request).then(callback ? callback : returnJsonIfOkOrThrow)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const postRequestStatus = async (url: string, payload: any, opts?: RequestInit) => {
  const request = new Request(url, {
    method: 'POST',
    headers: {
      Accept: contentType,
      Authorization: await getAuthHeader(),
      'Content-Type': contentType
    },
    body: JSON.stringify(payload),
    ...opts
  })
  return fetch(request).then((response) => response.status)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const putRequest = async (url: string, payload: any, opts?: RequestInit) => {
  const request = new Request(url, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
      Authorization: await getAuthHeader()
    },
    body: JSON.stringify(payload),
    ...opts
  })
  return fetch(request).then(returnJsonIfOkOrThrow)
}

interface UploadFileRequestParams {
  url: string
  file: File
  headers: Record<string, string | undefined>
  options?: UploadFileOptions
  progressCallBack?: (event: ProgressEvent) => void
  loadendCallBack?: (event: ProgressEvent) => void
  signal?: AbortSignal
}

const uploadFileRequest = async ({
  url,
  file,
  headers,
  options = {},
  progressCallBack,
  loadendCallBack,
  signal
}: UploadFileRequestParams): Promise<void> => {
  const { contentType, cacheControl } = options
  const xhr = new XMLHttpRequest()
  xhr.open('PUT', url)
  xhr.setRequestHeader('Content-Type', contentType || file.type)
  xhr.setRequestHeader('Cache-Control', cacheControl || 'no-cache')

  for (const [key, val] of Object.entries(headers)) {
    if (val !== undefined) {
      xhr.setRequestHeader(key, val)
    }
  }
  if (progressCallBack !== undefined) {
    xhr.upload.onprogress = progressCallBack
  }
  if (loadendCallBack !== undefined) {
    xhr.onloadend = loadendCallBack
  }

  if (signal) {
    signal.onabort = () => {
      xhr.abort()
      signal.onabort = null
    }
  }

  return new Promise<void>((resolve, reject) => {
    xhr.onload = () => {
      resolve()
    }
    xhr.onabort = () => {
      reject(new AbortError(signal?.aborted ? 'Request was aborted by user' : 'Request was aborted'))
    }
    xhr.onerror = () => {
      reject(new Error('Upload failed'))
    }
    xhr.send(file)
  })
}

const deleteRequest = async (url: string, opts?: RequestInit) => {
  const request = new Request(
    url,
    await buildRequestInit(opts, {
      method: 'DELETE',
      headers: {
        Authorization: await getAuthHeader()
      }
    })
  )
  return fetch(request).then(returnJsonIfOkOrThrow)
}

const deleteRequestStatus = async (url: string, opts?: RequestInit) => {
  const request = new Request(url, {
    method: 'DELETE',
    headers: {
      Authorization: await getAuthHeader()
    },
    ...opts
  })
  return fetch(request).then((response) => response.status)
}

const patchRequest = async <T = Record<string, unknown>>(url: string, payload: T, opts?: RequestInit) => {
  const request = new Request(url, {
    method: 'PATCH',
    headers: {
      Accept: contentType,
      Authorization: await getAuthHeader(),
      'Content-Type': contentType
    },
    body: JSON.stringify(payload),
    ...opts
  })
  return fetch(request).then(returnJsonIfOkOrThrow)
}

const patchRequestStatus = async (url: string, payload: Record<string, unknown>, opts?: RequestInit) => {
  const request = new Request(url, {
    method: 'PATCH',
    headers: {
      Accept: contentType,
      Authorization: await getAuthHeader(),
      'Content-Type': contentType
    },
    body: JSON.stringify(payload),
    ...opts
  })
  return fetch(request).then((response) => response.status)
}

/**
 * Merge custom and optional opts.
 *
 *  - add default auth if not overriden in `opts.headers`
 */
const buildRequestInit = async (
  opts: RequestOpts | undefined | RequestInit | null,
  defaultOpts: RequestOpts
): Promise<RequestInit> => {
  const anonymous = (opts as RequestOpts)?.anonymous
  const headers: Record<string, unknown> = {
    ...defaultOpts.headers,
    ...opts?.headers
  }
  if (!anonymous) {
    headers.Authorization = (opts?.headers as Record<string, string>)?.Authorization || (await getAuthHeader())
  }
  return {
    ...defaultOpts,
    ...opts,
    headers: headers as HeadersInit
  } as RequestInit
}

class AbortError extends Error {
  constructor(message?: string) {
    super(message || 'Request was aborted')
    this.name = 'AbortError'
  }
}

export {
  getToken,
  hasAuthToken,
  setGetTokenFn,
  setGetAuthHeaderFn,
  getRequest,
  getRequestFile,
  getRequestStatus,
  postRequest,
  postRequestFile,
  postRequestStatus,
  putRequest,
  deleteRequest,
  deleteRequestStatus,
  patchRequest,
  patchRequestStatus,
  uploadFileRequest,
  AbortError
}
